import { LngLatBounds, Map as MapLibreMap, Marker, NavigationControl, Popup, } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import ElementController from "../base-controllers/ElementController.js";
import { gettext, interpolate, ngettext } from "../utils/i18n.js";
const MAP_STYLE_URL = "https://tiles.openfreemap.org/styles/liberty";
const MAP_TILEJSON_URL = "https://tiles.openfreemap.org/planet";
/** Add localized labels and operational context while retaining OpenFreeMap's style. */
export function preparePlanningMapStyle(style, tileUrls) {
    const prepared = structuredClone(style);
    prepared.sources.openmaptiles = {
        type: "vector",
        tiles: tileUrls,
        maxzoom: 14,
        attribution: '<a href="https://openfreemap.org/">OpenFreeMap</a> © <a href="https://openmaptiles.org/">OpenMapTiles</a> Data from <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    };
    const irrelevantRoadShields = new Set([
        "highway-shield-non-us",
        "highway-shield-us-interstate",
        "road_shield_us",
    ]);
    prepared.layers = prepared.layers.filter((layer) => !irrelevantRoadShields.has(layer.id));
    const language = document.documentElement.lang.split("-")[0] || "sv";
    for (const layer of prepared.layers) {
        if (layer.type === "symbol" &&
            layer.source === "openmaptiles" &&
            layer.layout?.["text-field"]) {
            const fallback = layer.layout["text-field"];
            // Legacy stop functions are not valid nested expression operands.
            if (typeof fallback !== "string" && !Array.isArray(fallback))
                continue;
            layer.layout["text-field"] = [
                "coalesce",
                ["get", `name:${language}`],
                ["get", "name"],
                fallback,
            ];
        }
    }
    const firstLabel = prepared.layers.findIndex((layer) => layer.type === "symbol");
    const insertionPoint = firstLabel < 0 ? prepared.layers.length : firstLabel;
    prepared.layers.splice(insertionPoint, 0, {
        id: "delivery-municipality-boundaries",
        type: "line",
        source: "openmaptiles",
        "source-layer": "boundary",
        minzoom: 7,
        filter: [
            "all",
            [">=", ["get", "admin_level"], 7],
            ["<=", ["get", "admin_level"], 8],
            ["!=", ["get", "maritime"], 1],
        ],
        paint: {
            "line-color": "#64748b",
            "line-dasharray": [3, 2],
            "line-opacity": 0.85,
            "line-width": ["interpolate", ["linear"], ["zoom"], 7, 0.75, 13, 1.5],
        },
    }, {
        id: "delivery-house-numbers",
        type: "symbol",
        source: "openmaptiles",
        "source-layer": "housenumber",
        minzoom: 14,
        layout: {
            "text-field": ["get", "housenumber"],
            "text-font": ["Noto Sans Regular"],
            "text-size": 11,
        },
        paint: {
            "text-color": "#475569",
            "text-halo-color": "#ffffff",
            "text-halo-width": 1,
        },
    });
    return prepared;
}
/** Shared style loading for delivery planning and small place maps. */
export async function loadMapStyle(signal) {
    const [styleResponse, tileJsonResponse] = await Promise.all([
        fetch(MAP_STYLE_URL, { signal }),
        fetch(MAP_TILEJSON_URL, { signal }),
    ]);
    if (!styleResponse.ok || !tileJsonResponse.ok) {
        throw new Error(gettext("Could not read the map style"));
    }
    const tileJson = (await tileJsonResponse.json());
    if (!tileJson.tiles?.length)
        throw new Error(gettext("Map tiles are missing"));
    return preparePlanningMapStyle((await styleResponse.json()), tileJson.tiles);
}
/** Optional map view for selecting the same delivery bookings as the primary list. */
class DeliveryPlanningMap extends ElementController {
    constructor() {
        super(...arguments);
        this.map = null;
        this.mapAbortController = null;
        this.markers = new globalThis.Map();
        this.viewSelected = (event) => {
            const button = event.currentTarget;
            this.setView(button.dataset.deliveryPlanningView === "map" ? "map" : "list");
        };
        this.selectionChanged = () => {
            this.updateSelection();
        };
        this.mapOrderSelected = (event) => {
            const button = event.currentTarget;
            const orderId = button.dataset.mapSelectOrder;
            if (orderId)
                this.toggleOrder(orderId);
        };
    }
    init() {
        this.setView("list");
        this.updateSelection();
        this.updateMapNote();
    }
    destroy() {
        this.mapAbortController?.abort();
        this.mapAbortController = null;
        this.map?.remove();
        this.map = null;
        this.markers.clear();
        super.destroy();
    }
    setView(view) {
        const showMap = view === "map";
        const list = this.root.querySelector("[data-delivery-planning-list]");
        const map = this.root.querySelector("[data-delivery-planning-map]");
        const listControl = this.root.querySelector("[data-delivery-planning-list-control]");
        if (list)
            list.hidden = showMap;
        if (map)
            map.hidden = !showMap;
        if (listControl)
            listControl.hidden = showMap;
        for (const button of this.root.querySelectorAll("[data-delivery-planning-view]")) {
            const selected = button.dataset.deliveryPlanningView === view;
            button.setAttribute("aria-pressed", String(selected));
            button.dataset.variant = selected ? "secondary" : "ghost";
        }
        if (showMap) {
            map?.scrollIntoView({ block: "center" });
            this.ensureMap();
        }
    }
    deliveryRows() {
        return Array.from(this.root.querySelectorAll("[data-map-order]"));
    }
    deliveryPoints() {
        return this.deliveryRows().flatMap((row) => {
            const latitude = Number(row.dataset.mapLatitude);
            const longitude = Number(row.dataset.mapLongitude);
            const id = row.dataset.mapOrder;
            if (!id || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
                return [];
            }
            return [
                {
                    id,
                    title: row.dataset.mapTitle ?? id,
                    address: row.dataset.mapAddress ?? "",
                    latitude,
                    longitude,
                },
            ];
        });
    }
    async ensureMap() {
        if (this.map ||
            this.mapAbortController ||
            this.root.hasAttribute("data-map-disabled")) {
            return;
        }
        const container = this.root.querySelector("[data-delivery-planning-map-canvas]");
        if (!container)
            return;
        this.mapAbortController = new AbortController();
        try {
            const style = await loadMapStyle(this.mapAbortController.signal);
            const points = this.deliveryPoints();
            const onlyPoint = points.length === 1 ? points[0] : null;
            this.map = new MapLibreMap({
                container,
                style,
                center: onlyPoint
                    ? [onlyPoint.longitude, onlyPoint.latitude]
                    : [15.2, 62],
                zoom: onlyPoint ? 14 : 4,
                attributionControl: {},
            });
            this.map.addControl(new NavigationControl({ showCompass: false }), "top-right");
            this.map.once("load", () => {
                this.addMarkers(points);
                this.map?.resize();
            });
        }
        catch (error) {
            if (!(error instanceof DOMException && error.name === "AbortError")) {
                const errorMessage = this.root.querySelector("[data-delivery-planning-map-error]");
                if (errorMessage)
                    errorMessage.hidden = false;
            }
        }
        finally {
            this.mapAbortController = null;
        }
    }
    addMarkers(points) {
        if (!this.map || points.length === 0)
            return;
        const bounds = new LngLatBounds();
        for (const [index, point] of points.entries()) {
            const button = document.createElement("button");
            button.type = "button";
            button.className = "delivery-planning-map__marker";
            button.dataset.mapSelectOrder = point.id;
            button.setAttribute("aria-label", interpolate(gettext("Select %(title)s, %(address)s"), {
                title: point.title,
                address: point.address,
            }));
            button.textContent = String(index + 1);
            const popupContent = document.createElement("div");
            const title = document.createElement("strong");
            title.textContent = point.title;
            const address = document.createElement("p");
            address.textContent = point.address;
            popupContent.append(title, address);
            const marker = new Marker({ element: button, anchor: "bottom" })
                .setLngLat([point.longitude, point.latitude])
                .setPopup(new Popup({ offset: 20 }).setDOMContent(popupContent))
                .addTo(this.map);
            this.markers.set(point.id, { button, marker });
            bounds.extend([point.longitude, point.latitude]);
        }
        if (points.length === 1) {
            this.map.setCenter([points[0].longitude, points[0].latitude]);
            this.map.setZoom(14);
        }
        else {
            this.map.fitBounds(bounds, { padding: 56, maxZoom: 13, duration: 0 });
        }
        this.updateSelection();
    }
    toggleOrder(orderId) {
        const input = Array.from(this.root.querySelectorAll("[data-pick-order]")).find((candidate) => candidate.value === orderId);
        if (!input)
            return;
        input.checked = !input.checked;
        input.dispatchEvent(new Event("change", { bubbles: true }));
        input.focus({ preventScroll: true });
        this.updateSelection();
    }
    updateSelection() {
        const selected = new Set(Array.from(this.root.querySelectorAll("[data-pick-order]:checked")).map((input) => input.value));
        for (const row of this.deliveryRows()) {
            row.toggleAttribute("data-map-selected", selected.has(row.dataset.mapOrder ?? ""));
        }
        for (const [orderId, marker] of this.markers) {
            marker.button.toggleAttribute("data-selected", selected.has(orderId));
            marker.button.setAttribute("aria-pressed", String(selected.has(orderId)));
        }
    }
    updateMapNote() {
        const note = this.root.querySelector("[data-delivery-planning-map-note]");
        if (!note)
            return;
        const missing = this.deliveryRows().length - this.deliveryPoints().length;
        note.hidden = missing === 0;
        if (missing > 0) {
            note.textContent = interpolate(ngettext("%(count)s booking has no map position and remains in the list.", "%(count)s bookings have no map position and remain in the list.", missing), { count: missing });
        }
    }
}
DeliveryPlanningMap.id = "DeliveryPlanningMap";
DeliveryPlanningMap.binds = {
    "[data-delivery-planning-view]": [["click", "viewSelected"]],
    "[data-pick-order]": [["change", "selectionChanged"]],
    "[data-map-select-order]": [["click", "mapOrderSelected"]],
};
export default DeliveryPlanningMap;
//# sourceMappingURL=DeliveryPlanningMap.js.map